/**
 * Reads the current tenant's theme (row_hover + accent) from auth context
 * and mirrors it onto the document root as CSS variables:
 *   --ll-row-hover   -> table-row hover highlight
 *   --ll-accent-soft -> optional soft accent used elsewhere
 *
 * Defaults (when the tenant hasn't set a theme) match the hard-coded blue
 * that shipped with the first revision of the Reclaim/Bulk UI.
 * Mounted once at the app root (see App.js).
 */
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";

const DEFAULT_ROW_HOVER = "#EFF6FF"; // Tailwind blue-50
const DEFAULT_ACCENT = "#DBEAFE";    // Tailwind blue-100

export default function ThemeApplier() {
  const { tenant } = useAuth();

  useEffect(() => {
    const root = document.documentElement;
    const rowHover = tenant?.theme_row_hover || DEFAULT_ROW_HOVER;
    const accent = tenant?.theme_accent || DEFAULT_ACCENT;
    root.style.setProperty("--ll-row-hover", rowHover);
    root.style.setProperty("--ll-accent-soft", accent);
    // Also override the historic `--ll-mint` accent so every page that
    // predates the theme picker (Client Portal, hover-mint buttons, etc.)
    // picks up the firm's palette without a widespread code touch.
    // Only override when a theme is explicitly set — otherwise leave
    // --ll-mint at its CSS-defined default so the look is unchanged.
    if (tenant?.theme_accent) {
      root.style.setProperty("--ll-mint", tenant.theme_accent);
    } else {
      root.style.removeProperty("--ll-mint");
    }
  }, [tenant?.theme_row_hover, tenant?.theme_accent]);

  return null;
}
